import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { BarChart3, Loader2, TrendingUp, Phone, AlertTriangle, CheckCircle2, ExternalLink, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateRangePicker } from '../components/ui/date-range-picker';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const PIE_COLORS = ['#22D3C5', '#2BB6FF', '#FBBF24', '#FB7185', '#34D399', '#A6B2C7'];
// Fixed colors per analysis source so Claude vs Gemma are visually distinct
// across the Score Breakdown bars and the Score Trend chart.
const SCORE_SOURCE_COLORS = { claude: '#22D3C5', gemma: '#2BB6FF' };
const AUDIO_PAGE_SIZE_OPTIONS = [10, 20, 50];
// Call Purposes is restricted to these six canonical categories. The model is
// asked to emit these keys, but any unrecognized value is folded into "Other"
// so the chart only ever shows this fixed set. Order controls slice/legend order.
const PURPOSE_ORDER = ['health_enquiry', 'appointment_booking', 'follow_up', 'lead', 'complaint', 'other'];
const PURPOSE_LABELS = {
  health_enquiry: 'Health Enquiry',
  appointment_booking: 'Appointment Booking',
  follow_up: 'Follow Up',
  lead: 'Lead',
  complaint: 'Complaint',
  other: 'Other',
};
// Normalize a raw call_purpose to one of the six canonical keys; unknown → other.
function normalizePurpose(raw) {
  const key = String(raw || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return key !== 'other' && PURPOSE_LABELS[key] ? key : 'other';
}
// Call Outcomes chart only shows these four canonical outcomes.
const OUTCOME_ORDER = ['resolved', 'call_back_scheduled', 'unresolved', 'transferred'];
const OUTCOME_LABELS = {
  resolved: 'Resolved',
  call_back_scheduled: 'Call Back Scheduled',
  unresolved: 'Unresolved',
  transferred: 'Transferred',
};
// Map a raw call_outcome to one of the four canonical keys; unrelated outcomes → null (dropped).
function normalizeOutcome(raw) {
  const key = String(raw || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!key) return null;
  if (OUTCOME_LABELS[key]) return key;
  if (key.includes('call_back') || key.includes('callback')) return 'call_back_scheduled';
  if (key.includes('unresolved') || key.includes('not_resolved') || key.includes('no_resolution')) return 'unresolved';
  if (key.includes('transfer')) return 'transferred';
  if (key.includes('resolved')) return 'resolved';
  return null;
}
const SCORE_RANGE_OPTIONS = ['1-10', '11-20', '21-30', '31-40', '41-50', '51-60', '61-70', '71-80', '81-90', '91-100'];
const agentsListCacheByToken = new Map();
const agentsListPromiseByToken = new Map();

function getAuthCacheKey() {
  return sessionStorage.getItem('vcm_token') || 'anonymous';
}

async function getCachedAgentsList() {
  const cacheKey = getAuthCacheKey();

  if (agentsListCacheByToken.has(cacheKey)) {
    return agentsListCacheByToken.get(cacheKey);
  }

  if (!agentsListPromiseByToken.has(cacheKey)) {
    const requestPromise = api.getAgentsList().then((res) => {
      const agents = res?.agents || [];
      agentsListCacheByToken.set(cacheKey, agents);
      return agents;
    }).catch((error) => {
      agentsListPromiseByToken.delete(cacheKey);
      throw error;
    }).finally(() => {
      agentsListPromiseByToken.delete(cacheKey);
    });
    agentsListPromiseByToken.set(cacheKey, requestPromise);
  }

  return agentsListPromiseByToken.get(cacheKey);
}

// Does the (already-parsed) Claude transcript hold any real content?
function hasTranscriptContent(transcript) {
  if (!transcript) return false;
  if (typeof transcript === 'string') return transcript.trim() !== '';
  if (typeof transcript === 'object') {
    return (Array.isArray(transcript.segments) && transcript.segments.length > 0)
      || String(transcript.full_text || '').trim() !== '';
  }
  return false;
}

// Derive the Gemini (auto-processing) status the same way the backend
// /processing/gemini/calls endpoint does, so the Agent Analytics audio list
// reflects the Gemma pipeline rather than the Claude/manual processing_status.
// Returns a human label + badge classes covering every scenario: completed /
// failed / archived / in-flight stages / pending / ineligible.
function getGeminiStatusInfo(call) {
  const status = call.gemini_status || null;

  if (status === 'failed') return { label: 'Failed', cls: 'bg-rose-500/15 text-rose-300' };
  if (status === 'completed') return { label: 'Completed', cls: 'bg-emerald-500/15 text-emerald-300' };
  if (status === 'archived') return { label: 'Archived', cls: 'bg-slate-500/15 text-slate-300' };

  const hasTranscript = hasTranscriptContent(call.transcript);
  const hasGeminiTranscript = String(call.gemini_transcript || '').trim() !== '';
  const hasGeminiAnalysis = call.gemini_analysis != null;

  if (status === 'processing') {
    if (!hasTranscript) return { label: 'Transcribing', cls: 'bg-sky-500/15 text-sky-300 status-pulse' };
    if (!hasGeminiTranscript) return { label: 'Translating', cls: 'bg-amber-500/15 text-amber-300 status-pulse' };
    return { label: 'Analyzing', cls: 'bg-teal-500/15 text-teal-300 status-pulse' };
  }

  // gemini_status IS NULL → not yet picked up by the pipeline. Every such call
  // (whether eligible or not) is surfaced as "New" so the status badge matches
  // the "New" filter, which targets all calls awaiting Gemma processing.
  if (hasGeminiAnalysis) return { label: 'Completed', cls: 'bg-emerald-500/15 text-emerald-300' };
  return { label: 'New', cls: 'bg-blue-500/15 text-blue-300' };
}

// Derive the Claude (manual/legacy) pipeline status from processing_status.
// Used for the Status column when a call was analyzed by Claude, so the badge
// reflects the Claude run instead of the Gemma pipeline.
function getClaudeStatusInfo(call) {
  switch (call.processing_status) {
    case 'completed': return { label: 'Completed', cls: 'bg-emerald-500/15 text-emerald-300' };
    case 'failed': return { label: 'Failed', cls: 'bg-rose-500/15 text-rose-300' };
    case 'downloading': return { label: 'Downloading', cls: 'bg-sky-500/15 text-sky-300 status-pulse' };
    case 'transcribing': return { label: 'Transcribing', cls: 'bg-sky-500/15 text-sky-300 status-pulse' };
    case 'translating': return { label: 'Translating', cls: 'bg-amber-500/15 text-amber-300 status-pulse' };
    case 'analyzing': return { label: 'Analyzing', cls: 'bg-teal-500/15 text-teal-300 status-pulse' };
    case 'voice_analyzing': return { label: 'Analyzing', cls: 'bg-teal-500/15 text-teal-300 status-pulse' };
    default: return { label: 'New', cls: 'bg-blue-500/15 text-blue-300' };
  }
}

// A call counts as "analyzed by Claude" when call.analysis holds a real result
// (an overall_score). When true, the Status column shows the Claude status;
// otherwise it falls back to the Gemma pipeline status.
function isAnalyzedByClaude(call) {
  return Boolean(call.analysis && Object.keys(call.analysis).length > 0 && call.analysis.overall_score !== undefined);
}

function getScoreBadge(score) {
  if (score === null || score === undefined) return { cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30', label: 'N/A' };
  if (score >= 90) return { cls: 'score-excellent', label: 'Excellent' };
  if (score >= 75) return { cls: 'score-good', label: 'Good' };
  if (score >= 60) return { cls: 'score-review', label: 'Needs Review' };
  return { cls: 'score-critical', label: 'Critical' };
}

// Local-date YYYY-MM-DD helpers for the default analytics window (mirrors the Dashboard).
const toDateInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const todayInput = () => toDateInput(new Date());
const daysAgoInput = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateInput(d);
};

const monthsAgoInput = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return toDateInput(d);
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-2 shadow-lg">
      <p className="text-xs font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
        </p>
      ))}
    </div>
  );
}

// One Score Breakdown card for a single analysis source (Claude or Gemma).
function ScoreBreakdownCard({ title, accentColor, metrics, testId }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accentColor }} />
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{title}</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
        {metrics.map(item => {
          const pct = Math.min(100, ((item.value || 0) / 10) * 100);
          const barColor = pct >= 75 ? '#34D399' : pct >= 50 ? '#FBBF24' : '#FB7185';
          return (
            <div key={item.label}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">{item.label}</span>
                <span className="font-medium tabular-nums">{item.value != null ? `${item.value.toFixed(1)}/10` : '-'}</span>
              </div>
              <div className="h-2 bg-border rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// One Score Trend chart for a single analysis source (Claude or Gemma).
function ScoreTrendCard({ title, accentColor, data, dataKey, testId }) {
  const hasData = data.some((d) => d[dataKey] != null);
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accentColor }} />
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{title}</h3>
      </div>
      {hasData ? (
        <div tabIndex={-1}>
          <ResponsiveContainer className="focus:outline-none focus-visible:outline-none focus:ring-0" width="100%" height={260}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,34%,20%)" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#A6B2C7' }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#A6B2C7' }} />
              <Tooltip content={<ChartTooltip />} />
              <Line type="monotone" dataKey={dataKey} stroke={accentColor} name={title} strokeWidth={2} dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No trend data</div>
      )}
    </div>
  );
}

export default function AgentAnalyticsPage() {
  const navigate = useNavigate();
  const { isSupervisor, user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => ({ from_date: daysAgoInput(7), to_date: todayInput() }));
  const [audioCalls, setAudioCalls] = useState([]);
  const [audioTotal, setAudioTotal] = useState(0);
  const [audioTotalPages, setAudioTotalPages] = useState(1);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioPage, setAudioPage] = useState(1);
  const [audioPageSize, setAudioPageSize] = useState(20);
  const [audioSearchDebounced, setAudioSearchDebounced] = useState('');
  const [audioFilters, setAudioFilters] = useState({
    search: '',
    status: '',
    direction: '',
    escalation: '',
    scoreRange: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [downloadCategory, setDownloadCategory] = useState('need_review');
  // True while the unified report is being downloaded, so the button shows its
  // loading state and stays disabled.
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadCSV = async () => {
    setIsDownloading(true);
    try {
      // Unified report: the backend picks Claude analysis per call when the
      // call was analyzed by Claude, otherwise it falls back to Gemma.
      const blob = await api.exportAudioRecordsCSV({
        agent_number: selectedAgent,
        from_date: dateRange.from_date || undefined,
        to_date: dateRange.to_date || undefined,
        category: downloadCategory,
      });
      if (!blob || blob.size === 0) {
        toast.error('No data found for the selected category');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audio_records_${downloadCategory}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch {
      toast.error('Failed to download report');
    } finally {
      setIsDownloading(false);
    }
  };

  const clearFilters = () => {
    setAudioFilters({
      search: '',
      status: '',
      direction: '',
      escalation: '',
      scoreRange: '',
      sortBy: 'created_at',
      sortOrder: 'desc',
    });
    setAudioPage(1);
  };

  useEffect(() => {
    let active = true;

    getCachedAgentsList()
      .then((agentsList) => {
        if (active) {
          setAgents(agentsList);
        }
      })
      .catch(() => { });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedAgent) return;
    setLoading(true);
    // Pass the selected from/to range to the API. When no range is selected the
    // empty strings let the backend fall back to its default window.
    api.getAgentAnalytics(selectedAgent, dateRange.from_date, dateRange.to_date)
      .then(res => setAnalytics(res))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [selectedAgent, dateRange.from_date, dateRange.to_date]);

  useEffect(() => {
    if (!selectedAgent) {
      setAudioCalls([]);
      setAudioTotal(0);
      setAudioTotalPages(1);
      return;
    }

    const timer = setTimeout(() => {
      setAudioSearchDebounced(audioFilters.search.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedAgent, audioFilters]);

  useEffect(() => {
    setAudioPage(1);
  }, [selectedAgent, dateRange.from_date, dateRange.to_date, audioPageSize, audioFilters.status, audioFilters.direction, audioFilters.escalation, audioFilters.scoreRange, audioFilters.sortBy, audioFilters.sortOrder, audioSearchDebounced]);

  useEffect(() => {
    if (!selectedAgent) {
      setAudioCalls([]);
      setAudioTotal(0);
      setAudioTotalPages(1);
      return;
    }

    let active = true;
    const fromDate = dateRange.from_date || undefined;
    const toDate = dateRange.to_date || undefined;

    const loadAgentAudioCalls = async () => {
      setAudioLoading(true);
      try {
        const parseScoreRange = (range) => {
          if (!range) return null;
          const [minStr, maxStr] = range.split('-');
          return { min: Number(minStr), max: Number(maxStr) };
        };
        const scoreRange = parseScoreRange(audioFilters.scoreRange);
        const response = await api.getCalls({
          page: audioPage,
          limit: audioPageSize,
          agent_number: selectedAgent,
          from_date: fromDate,
          to_date: toDate,
          has_recording: true,
          // The status filter targets the Gemini (auto-processing) pipeline, so
          // send it as `gemini_status` rather than the Claude `status` param.
          gemini_status: audioFilters.status || undefined,
          direction: audioFilters.direction || undefined,
          ...(audioFilters.escalation && { escalation_detected: audioFilters.escalation }),
          ...(scoreRange && { min_effective_score: scoreRange.min, max_effective_score: scoreRange.max }),
          search: audioSearchDebounced || undefined,
          sort_by: audioFilters.sortBy,
          sort_order: audioFilters.sortOrder,
        });

        const serverPage = Number(response.page || audioPage);
        const serverTotalPages = Math.max(1, Number(response.pages || 1));

        if (active) {
          setAudioCalls(response.calls || []);
          setAudioTotal(Number(response.total || 0));
          setAudioTotalPages(serverTotalPages);

          if (serverPage !== audioPage) {
            setAudioPage(serverPage);
          } else if (audioPage > serverTotalPages) {
            setAudioPage(serverTotalPages);
          }
        }
      } catch {
        if (active) {
          toast.error('Failed to load agent audio records');
          setAudioCalls([]);
          setAudioTotal(0);
          setAudioTotalPages(1);
        }
      } finally {
        if (active) {
          setAudioLoading(false);
        }
      }
    };

    loadAgentAudioCalls();

    return () => {
      active = false;
    };
  }, [selectedAgent, dateRange.from_date, dateRange.to_date, audioPage, audioPageSize, audioFilters.status, audioFilters.direction, audioFilters.escalation, audioFilters.scoreRange, audioFilters.sortBy, audioFilters.sortOrder, audioSearchDebounced]);

  const avg = analytics?.averages || {};
  const trendData = (analytics?.daily_trend || []).map(d => ({
    date: d._id,
    // Effective score per day: Claude when the call was analyzed by Claude,
    // otherwise Gemma. Backs the unified Score Trend chart.
    effScore: d.eff_avg_score,
    grammar: d.avg_grammar,
    professionalism: d.avg_professionalism,
    calls: d.total
  }));
  // Bucket raw purposes into the six canonical categories (unknown → Other),
  // then emit them in fixed order, dropping empty buckets.
  const purposeCounts = (analytics?.purposes || []).reduce((acc, d) => {
    const key = normalizePurpose(d._id);
    acc[key] = (acc[key] || 0) + (Number(d.count) || 0);
    return acc;
  }, {});
  const purposesData = PURPOSE_ORDER
    .filter((key) => purposeCounts[key] > 0)
    .map((key) => ({ name: PURPOSE_LABELS[key], value: purposeCounts[key] }));
  const outcomeCounts = (analytics?.outcomes || []).reduce((acc, d) => {
    const key = normalizeOutcome(d._id);
    if (key) acc[key] = (acc[key] || 0) + (Number(d.count) || 0);
    return acc;
  }, {});
  const outcomesData = OUTCOME_ORDER
    .filter((key) => outcomeCounts[key] > 0)
    .map((key) => ({ name: OUTCOME_LABELS[key], value: outcomeCounts[key] }));
  const directionsData = (analytics?.directions || []).reduce((acc, d) => {
    const dir = d._id?.toLowerCase().trim();
    if (dir === 'inbound' || dir === 'outbound') {
      acc.push({
        name: dir === 'inbound' ? 'Inbound' : 'Outbound',
        value: Number(d.count) || 0,
        key: dir
      });
    }
    return acc;
  }, []);


  const openCallDetailsInNewTab = (callId, e) => {
    e.stopPropagation();
    const detailPath = `/calls/${encodeURIComponent(callId)}`;

    // Open first to avoid popup blockers, then hydrate auth state in the new tab.
    const newTab = window.open('', '_blank');
    if (!newTab) {
      navigate(detailPath);
      return;
    }

    try {
      const token = sessionStorage.getItem('vcm_token');
      const userData = sessionStorage.getItem('vcm_user');
      if (token) {
        newTab.sessionStorage.setItem('vcm_token', token);
      }
      if (userData) {
        newTab.sessionStorage.setItem('vcm_user', userData);
      }
      newTab.location.href = detailPath;
    } catch (error) {
      newTab.location.href = detailPath;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agent Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Performance metrics by agent</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            className="bg-card border border-border rounded-lg px-3 py-2 text-sm min-w-[200px]"
            data-testid="agent-analytics-agent-select"
          >
            <option value="">Select Agent</option>
            {isSupervisor && user?.agent_number && (
              <option value={user.agent_number}>Supervisor ({user.agent_name || user.agent_number})</option>
            )}
            {agents.map(a => (
              <option key={a.agent_number} value={a.agent_number}>{a.agent_name || a.agent_number}</option>
            ))}
          </select>
          <DateRangePicker
            value={dateRange}
            showValue={!!(dateRange.from_date || dateRange.to_date)}
            placeholder="Select date range"
            presets={[
              { label: 'Month', from_date: monthsAgoInput(1), to_date: todayInput() },
              { label: '3 Months', from_date: monthsAgoInput(3), to_date: todayInput() },
              { label: '6 Months', from_date: monthsAgoInput(6), to_date: todayInput() },
            ]}
            onChange={(range) => setDateRange({ from_date: range.from_date, to_date: range.to_date })}
            onClear={() => setDateRange({ from_date: daysAgoInput(7), to_date: todayInput() })}
            buttonClassName="h-10"
            data-testid="agent-analytics-date-range"
          />
        </div>
      </div>

      {!selectedAgent ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Select an agent to view their performance analytics</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : analytics ? (
        <>
          {/* Agent KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <Phone className="w-4 h-4 text-teal-400 mb-2" />
              <p className="text-xl font-semibold tabular-nums">{analytics.total_calls}</p>
              <p className="text-xs text-muted-foreground">Total Calls</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mb-2" />
              <p className="text-xl font-semibold tabular-nums">{analytics.processed_calls ?? 0}</p>
              <p className="text-xs text-muted-foreground">Processed</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <TrendingUp className="w-4 h-4 text-sky-400 mb-2" />
              <p className="text-xl font-semibold tabular-nums">{avg.eff_avg_score != null ? avg.eff_avg_score.toFixed(1) : '-'}</p>
              <p className="text-xs text-muted-foreground">Avg Score</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <BarChart3 className="w-4 h-4 text-emerald-400 mb-2" />
              <p className="text-xl font-semibold tabular-nums">{avg.eff_avg_professionalism != null ? `${avg.eff_avg_professionalism.toFixed(1)}/10` : '-'}</p>
              <p className="text-xs text-muted-foreground">Professionalism</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <AlertTriangle className="w-4 h-4 text-rose-400 mb-2" />
              <p className="text-xl font-semibold tabular-nums">{analytics.escalations}</p>
              <p className="text-xs text-muted-foreground">Escalations</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <AlertTriangle className="w-4 h-4 text-amber-400 mb-2" />
              <p className="text-xl font-semibold tabular-nums">{analytics.blame_shifts}</p>
              <p className="text-xs text-muted-foreground">Blame Shifts</p>
            </div>
          </div>

          {/* Score Breakdown — unified, per-call effective metrics (Claude when
              the call was analyzed by Claude, otherwise Gemma). */}
          <ScoreBreakdownCard
            title="Score Breakdown"
            accentColor={SCORE_SOURCE_COLORS.claude}
            testId="agent-analytics-breakdown"
            metrics={[
              { label: 'Grammar', value: avg.eff_avg_grammar },
              { label: 'Professionalism', value: avg.eff_avg_professionalism },
              { label: 'Empathy', value: avg.eff_avg_empathy },
              { label: 'Communication', value: avg.eff_avg_communication },
              { label: 'Greeting', value: avg.eff_avg_greeting },
              { label: 'Closing', value: avg.eff_avg_closing },
              { label: 'Solution', value: avg.eff_avg_solution },
            ]}
          />

          {/* Score Trend — unified, per-call effective score over time. */}
          <ScoreTrendCard
            title="Score Trend"
            accentColor={SCORE_SOURCE_COLORS.claude}
            testId="agent-analytics-trend"
            data={trendData}
            dataKey="effScore"
          />

          {/* Distribution Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1 bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Call Direction</h3>
              {directionsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={directionsData} dataKey="value" cx="50%" cy="50%" outerRadius={70}>
                      {directionsData.map((entry, i) => (
                        <Cell key={i} fill={entry.key === 'inbound' ? '#0EA5E9' : '#14B8A6'} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-8">No data</p>}
              {directionsData.length > 0 && (
                <div className="mt-3 space-y-2">
                  {directionsData.map(d => {
                    const total = directionsData.reduce((sum, dir) => sum + dir.value, 0);
                    const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={d.key} className="flex items-center justify-between text-xs">
                        <span className={`px-1.5 py-0.5 rounded ${d.key === 'inbound' ? 'bg-sky-500/20 text-sky-400' : 'bg-teal-500/20 text-teal-400'}`}>
                          {d.name}
                        </span>
                        <span>{d.value} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="lg:col-span-1 bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Call Purposes</h3>
              {purposesData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={purposesData} dataKey="value" cx="50%" cy="50%" outerRadius={70}>
                      {purposesData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-8">No data</p>}
              {purposesData.length > 0 && (
                <div className="mt-3 space-y-2">
                  {purposesData.map((d, i) => {
                    const total = purposesData.reduce((sum, p) => sum + p.value, 0);
                    const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
                    const color = PIE_COLORS[i % PIE_COLORS.length];
                    return (
                      <div key={d.name} className="flex items-center justify-between text-xs">
                        <span className="px-1.5 py-0.5 rounded truncate max-w-[60%]" style={{ backgroundColor: `${color}33`, color }}>
                          {d.name}
                        </span>
                        <span>{d.value} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="lg:col-span-1 bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Call Outcomes</h3>
              {outcomesData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={outcomesData} margin={{ top: 5, right: 5, left: 0, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,34%,20%)" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: '#A6B2C7' }}
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      height={50}
                      tickFormatter={(value) => (value && value.length > 12 ? `${value.slice(0, 12)}…` : value)}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#A6B2C7' }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="value" fill="#2BB6FF" radius={[4, 4, 0, 0]} minPointSize={6} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-8">No data</p>}
            </div>
          </div>

          {/* Agent Audio Records */}
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="agent-analytics-audio-list">
            <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Audio Records</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {audioLoading ? 'Loading records...' : `${audioTotal} matching audio records`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Download Report:</span>
                <select
                  value={downloadCategory}
                  onChange={(e) => setDownloadCategory(e.target.value)}
                  disabled={isDownloading}
                  className="bg-background border border-border rounded-lg px-2 py-1 text-xs disabled:opacity-50"
                >
                  <option value="need_review">Needs Review</option>
                  <option value="critical">Critical</option>
                </select>

                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleDownloadCSV()}
                  disabled={isDownloading}
                  className="flex items-center gap-1 text-xs"
                  data-testid="agent-analytics-download-report"
                >
                  {isDownloading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  {isDownloading ? 'Downloading...' : 'Report'}
                </Button>
                {!audioLoading && audioTotal > 0 && (
                  <div className="flex items-center gap-2 pl-1 border-l border-border">
                    <span className="text-xs text-muted-foreground">Rows per page</span>
                    <select
                      value={audioPageSize}
                      onChange={(e) => {
                        setAudioPageSize(Number(e.target.value));
                        setAudioPage(1);
                      }}
                      className="bg-background border border-border rounded-lg px-2 py-1 text-xs"
                    >
                      {AUDIO_PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="border-b border-border p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-9 gap-3">
              <Input
                value={audioFilters.search}
                onChange={(e) => {
                  setAudioFilters((prev) => ({ ...prev, search: e.target.value }));
                  setAudioPage(1);
                }}
                placeholder="Search call ID, caller, date"
                className="bg-background"
              />
              <select
                value={audioFilters.status}
                onChange={(e) => {
                  const nextStatus = e.target.value;
                  setAudioFilters((prev) => ({ ...prev, status: nextStatus }));
                  setAudioPage(1);
                }}
                className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
                  data-testid="agent-analytics-status-filter"
              >
                <option value="">Status</option>
                <option value="new">New</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={audioFilters.direction}
                onChange={(e) => {
                  setAudioFilters((prev) => ({ ...prev, direction: e.target.value }));
                  setAudioPage(1);
                }}
                className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
                data-testid="agent-analytics-direction-filter"
              >
                <option value="">Directions</option>
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
              <select
                value={audioFilters.escalation}
                onChange={(e) => {
                  setAudioFilters((prev) => ({ ...prev, escalation: e.target.value }));
                  setAudioPage(1);
                }}
                className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Escalations</option>
                <option value="true">Detected</option>
                <option value="false">Not Detected</option>
              </select>
              <select
                value={audioFilters.scoreRange}
                onChange={(e) => {
                  setAudioFilters((prev) => ({ ...prev, scoreRange: e.target.value }));
                  setAudioPage(1);
                }}
                className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
                data-testid="agent-analytics-score-filter"
              >
                <option value="">All Scores</option>
                {SCORE_RANGE_OPTIONS.map((range) => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
              <select
                value={audioFilters.sortBy}
                onChange={(e) => {
                  setAudioFilters((prev) => ({ ...prev, sortBy: e.target.value }));
                  setAudioPage(1);
                }}
                className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
              >
                <option value="effective_score">Sort: Score</option>
                <option value="created_at">Sort: Time</option>
                <option value="call_duration">Sort: Duration</option>
              </select>
              <select
                value={audioFilters.sortOrder}
                onChange={(e) => {
                  setAudioFilters((prev) => ({ ...prev, sortOrder: e.target.value }));
                  setAudioPage(1);
                }}
                className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
              <Button variant="outline" size="sm" onClick={clearFilters} className="lg:col-span-1 h-10"> Clear</Button>
            </div>

            <div className="border-b border-border px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_2fr_auto] gap-3">
              <span>Call ID</span>
              <span>Direction</span>
              <span>Duration</span>
              <span>Score</span>
              <span>Model</span>
              <span>Status</span>
              <span>Audio</span>
              <span className="text-right">Open</span>
            </div>

            {audioLoading ? (
              <div className="h-52 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : audioCalls.length === 0 ? (
              <div className="h-52 flex items-center justify-center text-sm text-muted-foreground">
                No audio records found for this agent and filter set.
              </div>
            ) : (
              <div>
                {audioCalls.map((call, idx) => {
                  const claudeScore = call.analysis?.overall_score;
                  const gemmaScore = call.gemini_analysis?.overall_score;
                  // Single combined score: Claude score when the call was analyzed
                  // by Claude, otherwise the Gemma score. The Model column labels
                  // which source the displayed score came from.
                  const analyzedByClaude = isAnalyzedByClaude(call);
                  const effectiveScore = analyzedByClaude ? claudeScore : gemmaScore;
                  const effectiveBadge = getScoreBadge(effectiveScore);
                  const modelLabel = analyzedByClaude ? 'Claude' : 'Gemma';
                  const openCallId = call.call_id || String(call.id || '');

                  return (
                    <div
                      key={call.call_id || `${call.id}-${idx}`}
                      className="border-b border-border/60 px-4 py-3 hover:bg-white/[0.02] cursor-pointer"
                      onClick={(e) => openCallId && openCallDetailsInNewTab(openCallId, e)}
                      data-testid="agent-analytics-audio-row"
                    >
                      <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_2fr_auto] gap-3 items-center">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{call.call_id || call.id}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {call.client_number || 'Unknown caller'} - {call.date} {call.time}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded w-fit ${call.direction === 'inbound' ? 'bg-sky-500/15 text-sky-300' : 'bg-teal-500/15 text-teal-300'}`}>
                          {call.direction}
                        </span>
                        <span className="text-xs tabular-nums text-muted-foreground">{call.call_duration || 0}s</span>
                        {effectiveScore !== null && effectiveScore !== undefined ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded w-fit ${effectiveBadge.cls}`} title={`${modelLabel}: ${effectiveBadge.label}`} data-testid="agent-analytics-score-badge">
                            {effectiveScore} - {effectiveBadge.label}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded w-fit ${analyzedByClaude ? 'bg-violet-500/15 text-violet-300' : 'bg-sky-500/15 text-sky-300'}`} data-testid="agent-analytics-model-badge">
                          {modelLabel}
                        </span>
                        {(() => {
                          // When Claude analyzed this call, surface the Claude
                          // status; otherwise fall back to the Gemma pipeline.
                          const status = isAnalyzedByClaude(call)
                            ? getClaudeStatusInfo(call)
                            : getGeminiStatusInfo(call);
                          return (
                            <span className={`text-xs px-2 py-0.5 rounded w-fit ${status.cls}`} data-testid="agent-analytics-status-badge">
                              {status.label}
                            </span>
                          );
                        })()}
                        <div onClick={(e) => e.stopPropagation()}>
                          <audio src={call.recording_url} controls className="w-full h-8" />
                        </div>
                        <Button size="sm" variant="ghost" className="justify-self-end" onClick={(e) => openCallDetailsInNewTab(openCallId, e)}
                          title="Open call detail" data-testid="agent-analytics-audio-open">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}

                <div className="px-4 py-3 border-t border-border flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    Showing {audioTotal === 0 ? 0 : (audioPage - 1) * audioPageSize + 1}
                    {' '}-{' '}
                    {Math.min((audioPage - 1) * audioPageSize + audioCalls.length, audioTotal)} of {audioTotal}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={audioPage <= 1}
                      onClick={() => setAudioPage((prev) => Math.max(1, prev - 1))}
                    >
                      Prev
                    </Button>
                    <span className="text-xs text-muted-foreground min-w-[80px] text-center">
                      Page {audioPage} / {audioTotalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={audioPage >= audioTotalPages}
                      onClick={() => setAudioPage((prev) => Math.min(audioTotalPages, prev + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </>
      ) : null}
    </div>
  );
}
